/**
 * The rail: vault-green sidebar with sub-menus (NFR-07). Desktop: collapsible to icons
 * (remembered). Phone/handheld: an off-canvas drawer opened from the top bar.
 */
import { ChevronDown, ChevronsLeft, ChevronsRight, Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cx } from '../../components/ui';
import { BRAND } from '../../config/brand';
import { useAuth } from '../../lib/auth';
import { useInstallPrompt } from '../../lib/pwa';
import { GROUPS, ROUTES, routeFor, type GroupId, type RouteDef } from '../routes';
import { Wordmark } from './Wordmark';

const OPEN_KEY = 'eros.nav.open';

export function Sidebar({ collapsed, onToggleCollapse, onNavigate }: { collapsed: boolean; onToggleCollapse?: () => void; onNavigate?: () => void }) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const { canInstall, install } = useInstallPrompt();
  const visible = ROUTES.filter((r) => r.nav && can(...r.perm));
  const activeGroup = routeFor(pathname)?.nav;
  const [open, setOpen] = useState<GroupId[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(OPEN_KEY) ?? '["inventory"]') as GroupId[];
    } catch {
      return ['inventory'];
    }
  });
  useEffect(() => {
    if (activeGroup && activeGroup !== 'top' && !open.includes(activeGroup)) setOpen((o) => [...o, activeGroup]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup]);
  const toggle = (g: GroupId) => {
    const next = open.includes(g) ? open.filter((x) => x !== g) : [...open, g];
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  // Menu order: Dashboard, Inventory, Pawn, Transfers, Sales, Money exchange, Audits, Reports, Admin, Help.
  const order: (GroupId | RouteDef)[] = [];
  const top = (path: string) => visible.find((r) => r.path === path);
  const push = (x: GroupId | RouteDef | undefined) => x && order.push(x);
  push(top('/'));
  push('inventory');
  push('pawn');
  push(top('/transfers'));
  push('sales');
  push('fx');
  push('audits');
  push(top('/reports'));
  push('admin');

  const leaf = (r: RouteDef, nested: boolean) => (
    <NavLink
      key={r.path}
      to={r.path}
      end={r.path === '/' || r.path === '/sales' || r.path === '/audits' || r.path === '/pawn'}
      onClick={onNavigate}
      title={collapsed ? r.title : undefined}
      className={({ isActive }) =>
        cx(
          'group relative flex min-h-touch items-center gap-3 rounded-tag px-3 text-[15px] transition-colors',
          nested && !collapsed && 'pl-11 text-sm',
          isActive ? 'bg-rail-hover font-semibold text-rail-ink' : 'text-rail-muted hover:bg-rail-hover/70 hover:text-rail-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span aria-hidden className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-brass" />}
          {(!nested || collapsed) && <r.icon aria-hidden className="h-[18px] w-[18px] shrink-0" />}
          {!collapsed && <span className="truncate">{r.title}</span>}
        </>
      )}
    </NavLink>
  );

  return (
    <nav aria-label="Main" className="flex h-full flex-col bg-rail text-rail-ink">
      <div className={cx('flex h-16 shrink-0 items-center border-b border-rail-line', collapsed ? 'justify-center px-2' : 'px-4')}>
        <Wordmark compact={collapsed} onDark />
      </div>
      <div className="scrollbar-thin flex-1 space-y-0.5 overflow-y-auto p-2">
        {order.map((x) => {
          if (typeof x !== 'string') return leaf(x, false);
          const g = GROUPS.find((gg) => gg.id === x)!;
          const kids = visible.filter((r) => r.nav === x);
          if (!kids.length) return null;
          const isOpen = open.includes(x) || collapsed;
          const hasActive = activeGroup === x;
          return (
            <div key={x} className={cx(collapsed && 'border-t border-rail-line pt-1 first:border-0')}>
              {!collapsed && (
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggle(x)}
                  className={cx('flex min-h-touch w-full items-center gap-3 rounded-tag px-3 text-[15px] transition-colors hover:bg-rail-hover/70', hasActive ? 'text-rail-ink' : 'text-rail-muted hover:text-rail-ink')}
                >
                  <g.icon aria-hidden className="h-[18px] w-[18px] shrink-0" />
                  <span className="flex-1 text-left">{g.label}</span>
                  <ChevronDown aria-hidden className={cx('h-4 w-4 transition-transform', isOpen ? 'rotate-0' : '-rotate-90')} />
                </button>
              )}
              {isOpen && <div className="space-y-0.5">{kids.map((r) => leaf(r, true))}</div>}
            </div>
          );
        })}
      </div>
      <div className="space-y-0.5 border-t border-rail-line p-2">
        {top('/help') && leaf(top('/help')!, false)}
        {canInstall && (
          <button type="button" onClick={() => void install()} title={collapsed ? 'Install app' : undefined} className="flex min-h-touch w-full items-center gap-3 rounded-tag px-3 text-[15px] text-rail-muted hover:bg-rail-hover/70 hover:text-rail-ink">
            <Download aria-hidden className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && 'Install app'}
          </button>
        )}
        {onToggleCollapse && (
          <button type="button" onClick={onToggleCollapse} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'} className="hidden min-h-touch w-full items-center gap-3 rounded-tag px-3 text-sm text-rail-muted hover:bg-rail-hover/70 hover:text-rail-ink lg:flex">
            {collapsed ? <ChevronsRight aria-hidden className="h-[18px] w-[18px]" /> : <ChevronsLeft aria-hidden className="h-[18px] w-[18px]" />}
            {!collapsed && 'Collapse'}
          </button>
        )}
        {!collapsed && <p className="px-3 pb-1 pt-2 text-xs text-rail-muted/80">{BRAND.operator}</p>}
      </div>
    </nav>
  );
}
